import { Select } from "antd";
import dayjs from "dayjs";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  IRuleCondition,
  IWorkflowRule,
  IWorkflowsSelectOption,
} from "../../../../../../../@types/Worflows";
import { ArrowDown } from "../../../../../../../assets/icons/ArrowDown";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../../components/SelectDropdown";
import { TimePickerInput } from "../../../../../../../components/TimePicker/TimePicker";
import { getWorkflowOptionLabel } from "../../../../../../../utils/workflowOptionLabels";
import { ErrorMessage, selectCss } from "../../../WorkflowsBuilder.styles";
import { SelectContainer, popupOptionsCss } from "../RuleComponent.styles";

interface IActivityFieldsProps {
  formik: FormikProps<IWorkflowRule>;
  ruleConditionComparators?: IWorkflowsSelectOption[];
  activityMetrics?: IWorkflowsSelectOption[];
}

export function ActivityFields({
  formik,
  ruleConditionComparators,
  activityMetrics,
}: IActivityFieldsProps) {
  const { t } = useTranslation();

  const shouldShowConditionError = (field: keyof IRuleCondition): string => {
    const touchedArray = formik.touched.conditions as
      | FormikTouched<IRuleCondition>[]
      | undefined;
    const errorArray = formik.errors.conditions as
      | FormikErrors<IRuleCondition>[]
      | undefined;

    if (touchedArray && errorArray) {
      const touched = touchedArray[0];
      const error = errorArray[0];

      return touched?.[field] && error?.[field] ? (error[field] as string) : "";
    }

    return "";
  };

  const metricOptions: IOption[] = useMemo(
    () =>
      activityMetrics?.map((metric) => ({
        label: getWorkflowOptionLabel("activityMetric", metric, t),
        value: metric.type,
        key: metric.type,
      })) ?? [],
    [activityMetrics, t]
  );

  // Activity rules expose only the simple comparators — every baseline-based comparator
  // (% from baseline, less/greater than baseline) is excluded.
  const comparatorOptions: IOption[] = useMemo(
    () =>
      ruleConditionComparators
        ?.filter((c) => !c.type.includes("Baseline"))
        .map((c) => ({
          label: getWorkflowOptionLabel("comparator", c, t),
          value: c.type,
          key: c.type,
        })) ?? [],
    [ruleConditionComparators, t]
  );

  // Threshold is stored in minutes; the picker edits it as hours:minutes.
  const thresholdMinutes = formik.values.conditions[0].threshold;
  const thresholdTimeValue =
    thresholdMinutes !== undefined && thresholdMinutes !== null
      ? dayjs()
          .hour(Math.floor(thresholdMinutes / 60))
          .minute(thresholdMinutes % 60)
      : undefined;

  const selectedMetric = formik.values.conditions[0].activityMetric;
  const selectedComparator =
    formik.values.conditions[0].conditionComparatorType;

  return (
    <>
      <SelectContainer
        isError={shouldShowConditionError("activityMetric") !== ""}
      >
        <p>{t("workflows_rule_activity")}</p>
        <Select
          placeholder={t("workflows_rule_sedentary")}
          value={selectedMetric}
          suffixIcon={<ArrowDown />}
          options={metricOptions}
          popupClassName={popupOptionsCss()}
          notFoundContent={t("workflows_rule_noData")}
          className={`${selectCss()} nopan nodrag`}
          onChange={(value) => {
            formik.setFieldValue("conditions[0].activityMetric", value);
          }}
        />
        <ErrorMessage>
          {shouldShowConditionError("activityMetric")}
        </ErrorMessage>
      </SelectContainer>

      {selectedMetric !== undefined ? (
        <SelectContainer
          isError={shouldShowConditionError("conditionComparatorType") !== ""}
        >
          <p>{t("workflows_rule_comparator")}</p>
          <SelectDropdown
            placeholder={t("workflows_rule_lessThan")}
            value={selectedComparator}
            options={comparatorOptions}
            onValueChange={(value) =>
              formik.setFieldValue(
                "conditions[0].conditionComparatorType",
                value as string
              )
            }
            className={`${selectCss()} nopan nodrag`}
          />
          <ErrorMessage>
            {shouldShowConditionError("conditionComparatorType")}
          </ErrorMessage>
        </SelectContainer>
      ) : null}

      {selectedComparator !== undefined ? (
        <SelectContainer isError={shouldShowConditionError("threshold") !== ""}>
          <p>{t("workflows_rule_thresholdHhMm")}</p>
          <TimePickerInput
            value={thresholdTimeValue}
            format="HH:mm"
            placeholder={t("workflows_rule_selectDuration")}
            hideTimezone
            onChange={(time, timeString) => {
              const value = Array.isArray(timeString)
                ? timeString[0]
                : timeString;

              if (!value) {
                formik.setFieldValue("conditions[0].threshold", undefined);
                return;
              }

              const [hours, minutes] = value
                .split(":")
                .map((part) => parseInt(part, 10));
              formik.setFieldValue(
                "conditions[0].threshold",
                hours * 60 + minutes
              );
            }}
          />
          <ErrorMessage>{shouldShowConditionError("threshold")}</ErrorMessage>
        </SelectContainer>
      ) : null}
    </>
  );
}
